import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, orderBy, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../App';
import { Appointment, UserProfile } from '../types';
import { Clipboard, User, Clock, ArrowRight, Save, CheckCircle2 } from 'lucide-react';
import { motion } from 'motion/react';

export default function Triage() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selected, setSelected] = useState<Appointment | null>(null);
  const [patient, setPatient] = useState<UserProfile | null>(null);
  const [triageNotes, setTriageNotes] = useState('');
  const [specialty, setSpecialty] = useState('Clínico Geral');
  const [doctors, setDoctors] = useState<UserProfile[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, 'appointments'),
      where('status', '==', 'waiting'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Appointment));
      setAppointments(docs);
      setLoading(false);
    });

    // Fetch doctors
    const qDoctors = query(collection(db, 'users'), where('role', '==', 'doctor'));
    const unsubscribeDoctors = onSnapshot(qDoctors, (snapshot) => {
      const docs = snapshot.docs.map(doc => doc.data() as UserProfile);
      setDoctors(docs);
    });

    return () => {
      unsubscribe();
      unsubscribeDoctors();
    };
  }, []);

  const handleSelect = async (appointment: Appointment) => {
    setSelected(appointment);
    setTriageNotes(appointment.triageNotes || '');
    setSpecialty(appointment.specialty || 'Clínico Geral');
    
    // Fetch patient info
    const patientDoc = await getDoc(doc(db, 'users', appointment.patientId));
    if (patientDoc.exists()) {
      setPatient(patientDoc.data() as UserProfile);
    }
  };

  const handleSaveTriage = async () => {
    if (!selected) return;
    try {
      await updateDoc(doc(db, 'appointments', selected.id), {
        status: 'triaged',
        triageNotes,
        specialty,
        doctorId: selectedDoctor || null,
        attendantId: user?.uid,
        triagedAt: new Date().toISOString()
      });
      setSelected(null);
      setPatient(null);
      setTriageNotes('');
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-text-main flex items-center">
          <Clipboard className="w-6 h-6 mr-2 text-primary" /> Triagem de Pacientes
        </h1>
        <p className="text-text-muted">Gerencie a fila de espera e encaminhe pacientes para os médicos.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Queue List */}
        <div className="lg:col-span-1">
          <section className="bg-white rounded-xl shadow-sm border border-text-muted/10 overflow-hidden">
            <div className="px-6 py-4 border-b border-text-muted/10">
              <h2 className="text-lg font-bold text-text-main">Fila de Espera ({appointments.length})</h2>
            </div>
            <div className="divide-y divide-text-muted/10 max-h-[600px] overflow-y-auto">
              {appointments.length === 0 ? (
                <div className="px-6 py-12 text-center text-text-muted">
                  Nenhum paciente aguardando.
                </div>
              ) : (
                appointments.map((appointment) => (
                  <button
                    key={appointment.id}
                    onClick={() => handleSelect(appointment)}
                    className={`w-full px-6 py-4 flex items-center justify-between hover:bg-bg-light transition-colors text-left ${
                      selected?.id === appointment.id ? 'bg-primary/10 border-l-4 border-primary' : ''
                    }`}
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-bg-light rounded-full flex items-center justify-center mr-4">
                        <User className="w-5 h-5 text-text-muted" />
                      </div>
                      <div>
                        <p className="font-medium text-text-main">Paciente #{appointment.patientId.slice(-4)}</p>
                        <p className="text-xs text-text-muted flex items-center">
                          <Clock className="w-3 h-3 mr-1" /> {new Date(appointment.createdAt).toLocaleTimeString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-text-muted/30" />
                  </button>
                ))
              )}
            </div>
          </section>
        </div>

        {/* Triage Form */}
        <div className="lg:col-span-2">
          {selected ? (
            <motion.section
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-sm border border-text-muted/10 p-8"
            >
              <h2 className="text-xl font-bold text-text-main mb-6 border-b border-text-muted/10 pb-4">
                Ficha de Triagem: {patient?.name || `Paciente #${selected.patientId.slice(-4)}`}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-1">CPF</p>
                  <p className="text-text-main font-medium">{patient?.cpf || 'Não informado'}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-1">Data de Nascimento</p>
                  <p className="text-text-main font-medium">{patient?.birthDate || 'Não informado'}</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-text-main/70 mb-2">Notas de Triagem (Sintomas, Alergias, etc.)</label>
                  <textarea
                    rows={4}
                    value={triageNotes}
                    onChange={(e) => setTriageNotes(e.target.value)}
                    className="block w-full border-text-muted/30 rounded-lg shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="Descreva os sintomas relatados pelo paciente..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-text-main/70 mb-2">Especialidade Necessária</label>
                    <select
                      value={specialty}
                      onChange={(e) => setSpecialty(e.target.value)}
                      className="block w-full border-text-muted/30 rounded-lg shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    >
                      <option value="Clínico Geral">Clínico Geral</option>
                      <option value="Cardiologia">Cardiologia</option>
                      <option value="Dermatologia">Dermatologia</option>
                      <option value="Pediatria">Pediatria</option>
                      <option value="Psiquiatria">Psiquiatria</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-main/70 mb-2">Encaminhar para Médico (Opcional)</label>
                    <select
                      value={selectedDoctor}
                      onChange={(e) => setSelectedDoctor(e.target.value)}
                      className="block w-full border-text-muted/30 rounded-lg shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    >
                      <option value="">Qualquer médico disponível</option>
                      {doctors.map((doc) => (
                        <option key={doc.uid} value={doc.uid}>{doc.name} ({doc.specialty || 'Clínico'})</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="pt-6 border-t border-text-muted/10 flex justify-end">
                  <button
                    onClick={handleSaveTriage}
                    className="inline-flex items-center px-6 py-3 bg-primary text-white font-bold rounded-lg hover:bg-primary-dark transition-colors shadow-md shadow-primary/20"
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" /> Concluir Triagem e Encaminhar
                  </button>
                </div>
              </div>
            </motion.section>
          ) : (
            <div className="bg-bg-light border-2 border-dashed border-text-muted/30 rounded-xl p-12 text-center text-text-muted">
              <Clipboard className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p className="text-lg">Selecione um paciente na fila para iniciar a triagem.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
